package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.motion.pressable
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.focusRingOutline
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

data class SegmentedOption<T>(
    val value: T,
    val label: String,
    val count: Int? = null,
)

private const val TRACK_PADDING_PX = 3
private const val SEGMENT_INDEX_ATTR = "data-seg-index"
private const val GROUP_SELECTOR = "[role=\"radiogroup\"]"
private const val SEGMENT_WEIGHT = 500
private const val COUNT_OPACITY = "0.7"

val SegmentedTrackStyle = CssStyle {
    base {
        Modifier
            .padding(TRACK_PADDING_PX.px)
            .styleModifier {
                property("position", "relative")
                property("display", "inline-grid")
                property("grid-auto-flow", "column")
                property("grid-auto-columns", "1fr")
                property("align-items", "center")
                property("flex-shrink", "0")
                property("border-radius", "${Radius.full}")
                property("background-color", Palette.surfaceSubtle.cssRgb())
                property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
            }
    }
}

val SegmentStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("position", "relative")
            property("z-index", "1")
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("gap", "${Spacing.xxs}")
            property("white-space", "nowrap")
            property("cursor", "pointer")
            property("user-select", "none")
            property("outline", "none")
            property("border-radius", "${Radius.full}")
            property("color", Palette.textSecondary.cssRgb())
            property("transition", Motion.transition(Motion.DUR_FAST, "color"))
        }
    }
    hover {
        Modifier.styleModifier { property("color", Palette.textPrimary.cssRgb()) }
    }
    cssRule("[aria-checked=\"true\"]") {
        Modifier.styleModifier { property("color", Palette.textPrimary.cssRgb()) }
    }
    focusVisible {
        Modifier.focusRingOutline()
    }
}

@Composable
fun <T> SegmentedControl(
    options: List<SegmentedOption<T>>,
    selected: T,
    onSelect: (T) -> Unit,
    ariaLabel: String,
    modifier: Modifier = Modifier,
) {
    val selectedIndex = options.indexOfFirst { it.value == selected }.coerceAtLeast(0)

    Div(
        attrs = SegmentedTrackStyle.toModifier()
            .then(modifier)
            .toAttrs {
                attr("role", "radiogroup")
                attr("aria-label", ariaLabel)
            },
    ) {
        Div(
            attrs = Modifier
                .classNames("sb-seg-thumb")
                .styleModifier {
                    property("position", "absolute")
                    property("top", "${TRACK_PADDING_PX}px")
                    property("bottom", "${TRACK_PADDING_PX}px")
                    property("left", "${TRACK_PADDING_PX}px")
                    property("width", "calc((100% - ${TRACK_PADDING_PX * 2}px) / ${options.size})")
                    property("border-radius", "${Radius.full}")
                    property("background-color", Palette.surface.cssRgb())
                    property("box-shadow", Shadow.SM)
                    property("pointer-events", "none")
                    property("transform", "translateX(${selectedIndex * 100}%)")
                }
                .toAttrs { attr("aria-hidden", "true") },
        )
        options.forEachIndexed { index, option ->
            SegmentItem(
                option = option,
                index = index,
                selected = index == selectedIndex,
                onKeyNavigate = { target -> onSelect(options[target].value) },
                optionCount = options.size,
                onSelect = { onSelect(option.value) },
            )
        }
    }
}

@Composable
private fun <T> SegmentItem(
    option: SegmentedOption<T>,
    index: Int,
    optionCount: Int,
    selected: Boolean,
    onSelect: () -> Unit,
    onKeyNavigate: (Int) -> Unit,
) {
    Div(
        attrs = SegmentStyle.toModifier()
            .pressable()
            .padding(topBottom = Spacing.xxs, leftRight = Spacing.sm)
            .fontSize(Typography.bodySmall.size)
            .fontWeight(SEGMENT_WEIGHT)
            .role("radio")
            .tabIndex(if (selected) 0 else -1)
            .attrsModifier {
                attr("aria-checked", selected.toString())
                attr(SEGMENT_INDEX_ATTR, index.toString())
            }
            .onClick { onSelect() }
            .onKeyDown { event ->
                val target = when (event.key) {
                    "ArrowRight", "ArrowDown" -> (index + 1) % optionCount
                    "ArrowLeft", "ArrowUp" -> (index - 1 + optionCount) % optionCount
                    "Home" -> 0
                    "End" -> optionCount - 1
                    else -> null
                }
                when {
                    target != null -> {
                        event.preventDefault()
                        onKeyNavigate(target)
                        val group = (event.target as? HTMLElement)?.closest(GROUP_SELECTOR)
                        focusAfterRender { group?.querySelector("[$SEGMENT_INDEX_ATTR=\"$target\"]") as? HTMLElement }
                    }
                    event.key == "Enter" || event.key == " " -> {
                        event.preventDefault()
                        onSelect()
                    }
                }
            }
            .toAttrs(),
    ) {
        SpanText(text = option.label)
        option.count?.let { count ->
            SpanText(
                text = count.toString(),
                modifier = Modifier.styleModifier {
                    property("opacity", COUNT_OPACITY)
                    property("font-variant-numeric", "tabular-nums")
                },
            )
        }
    }
}
